/**
 * Platform SaaS permissions — never assign to tenant roles / templates.
 * Must stay in sync with ApplicationPermissions.PlatformPermissionNames (C#).
 * Under multi-tenancy: check these only via canPlatform / HasPlatformPermission.
 */
export const PLATFORM_PERMISSION_NAMES = [
  'ManageTenants',
  'ManageTenantGroups',
  'ManageTenantUsers',
  'ManageSubscriptions',
  'ManageLicenses',
  'ManageRoleCatalog',
  'ManageBuildProfiles',
  'ViewCrossTenantAudit',
  'ImpersonateTenant',
] as const;

export type PlatformPermissionName = (typeof PLATFORM_PERMISSION_NAMES)[number];

const platformSet = new Set<string>(
  PLATFORM_PERMISSION_NAMES.map((x) => x.toLowerCase())
);

export function isPlatformPermission(name: string | null | undefined): boolean {
  if (!name) return false;
  return platformSet.has(name.toLowerCase());
}

export function filterOutPlatformPermissions<T extends { name?: string | null }>(
  permissions: T[]
): T[] {
  return permissions.filter((p) => !isPlatformPermission(p.name));
}

/** SaaS / legacy Identity roles: platform permission names (+ Administrator). */
export function filterOnlyPlatformPermissions<T extends { name?: string | null }>(
  permissions: T[]
): T[] {
  return permissions.filter(
    (p) =>
      isPlatformPermission(p.name) ||
      p.name === 'Administrator' ||
      p.name?.toLowerCase() === 'administrator'
  );
}

export type AdminMode = 'saas' | 'tenant';

export const ADMIN_MODE_STORAGE_KEY = 'hermesz.cms.adminMode';

export function readStoredAdminMode(): AdminMode | null {
  try {
    const v = localStorage.getItem(ADMIN_MODE_STORAGE_KEY);
    if (v === 'saas' || v === 'tenant') return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function writeStoredAdminMode(mode: AdminMode) {
  try {
    localStorage.setItem(ADMIN_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function userHasPlatformSaaSAccess(
  platformPermissions: string[] | null | undefined
): boolean {
  if (!Array.isArray(platformPermissions) || platformPermissions.length === 0) {
    return false;
  }
  return platformPermissions.some(
    (p) =>
      isPlatformPermission(p) ||
      p === 'Administrator' ||
      p?.toLowerCase() === 'administrator'
  );
}
